import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import {
  bodyError,
  describeTranscriptionError,
  type TranscriptionProvider,
  type TranscriptionTestResult,
} from "@/lib/transcriptionProviderLogic";

/** Error carrying the backend's machine-readable `code` (test_required, key_reentry_required, ...). */
export class TranscriptionApiError extends Error {
  code?: string;
  constructor(message: string, code?: string) {
    super(message);
    this.name = "TranscriptionApiError";
    this.code = code;
  }
}

async function call<T>(body: Record<string, unknown>): Promise<T> {
  const { data, error } = await supabase.functions.invoke("llm-providers", { body: { ...body, kind: "transcription" } });
  if (error) {
    // Non-2xx: the JSON body ({error, code}) is on error.context (a Response).
    let code: string | undefined;
    let message: string | undefined;
    try {
      const ctx = (error as { context?: Response }).context;
      const parsed = ctx && typeof ctx.json === "function" ? await ctx.json() : null;
      if (parsed && typeof parsed === "object") {
        code = typeof parsed.code === "string" ? parsed.code : undefined;
        message = typeof parsed.error === "string" ? parsed.error : undefined;
      }
    } catch { /* body not JSON */ }
    throw new TranscriptionApiError(describeTranscriptionError(code, message ?? error.message), code);
  }
  const apiError = bodyError(data);
  if (apiError) throw new TranscriptionApiError(describeTranscriptionError(apiError.code, apiError.message), apiError.code);
  return data as T;
}

export interface TranscriptionSaveInput {
  id?: string;
  label: string;
  preset: string;
  baseUrl: string;
  apiKey?: string;
  defaultModel: string;
  testToken: string;
}

export function useTranscriptionProviders() {
  const [providers, setProviders] = useState<TranscriptionProvider[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async (opts?: { quiet?: boolean }) => {
    if (!opts?.quiet) setLoading(true);
    setError(null);
    try {
      const { providers } = await call<{ providers: TranscriptionProvider[] }>({ action: "list" });
      setProviders(providers);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load transcription providers");
    } finally {
      if (!opts?.quiet) setLoading(false);
    }
  }, []);

  useEffect(() => { void refresh(); }, [refresh]);

  const testProvider = useCallback(
    (input: { id?: string; baseUrl: string; apiKey?: string; defaultModel: string }) =>
      call<TranscriptionTestResult>({ action: "test", ...input }),
    [],
  );

  const saveProvider = useCallback(
    async (input: TranscriptionSaveInput) => {
      const r = await call<{ provider: TranscriptionProvider }>({ action: "save", ...input });
      await refresh({ quiet: true });
      return r;
    },
    [refresh],
  );

  const setActive = useCallback(
    async (id: string) => { await call({ action: "set_active", id }); await refresh({ quiet: true }); },
    [refresh],
  );

  const deactivate = useCallback(
    async (id: string) => { await call({ action: "deactivate", id }); await refresh({ quiet: true }); },
    [refresh],
  );

  const deleteProvider = useCallback(
    async (id: string) => { await call({ action: "delete", id }); await refresh({ quiet: true }); },
    [refresh],
  );

  return { providers, loading, error, refresh, testProvider, saveProvider, setActive, deactivate, deleteProvider };
}
